import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getTokenTransfers } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRightLeft, Loader2 } from "lucide-react";

const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 6 });
const short = (a) => `${a.slice(0, 6)}...${a.slice(-4)}`;
const ZERO = "0x0000000000000000000000000000000000000000";
const TYPE_STYLE = { mint: "bg-green-500/10 text-green-400", burn: "bg-red-500/10 text-red-400", transfer: "bg-[#D4AF37]/10 text-[#D4AF37]" };

const timeAgo = (ts) => {
  if (!ts) return "—";
  const d = Math.max(Math.floor(Date.now() / 1000 - ts), 0);
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(ts * 1000).toLocaleDateString();
};

const Party = ({ address }) =>
  address === ZERO ? <span className="font-mono text-xs text-zinc-600">0x0 (mint/burn)</span> : (
    <Link to={`/address/${address}`} className="table-link font-mono text-xs">{short(address)}</Link>
  );

export const TokenTransfers = ({ address }) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["tokenTransfers", address],
    queryFn: ({ pageParam }) => getTokenTransfers(address, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    refetchInterval: 10000,
  });
  const pages = data?.pages || [];
  const rows = pages.flatMap((p) => p.transfers);
  const symbol = pages[0]?.symbol;

  return (
    <Card data-testid="token-transfers" className="border-[#2A2411] bg-[#0A0A0A] mt-6">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2"><ArrowRightLeft size={16} className="text-[#D4AF37]" /> Token Transfers</CardTitle>
        {pages[0] && <span data-testid="token-transfers-total" className="text-xs text-zinc-500">{pages[0].total.toLocaleString()} total</span>}
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="text-sm text-zinc-500 px-6 pb-6">No transfers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="token-transfers-table" className="w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-500 text-left border-b border-[#2A2411]">
                  {["Tx Hash", "Age", "Type", "From", "To"].map((h) => <th key={h} className="px-4 py-2 font-normal">{h}</th>)}
                  <th className="px-4 py-2 font-normal text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t, i) => (
                  <tr key={`${t.txHash}-${i}`} className="border-b border-[#2A2411]">
                    <td className="px-4 py-2"><Link to={`/tx/${t.txHash}`} className="table-link font-mono text-xs">{t.txHash.slice(0, 10)}…</Link></td>
                    <td className="px-4 py-2 text-xs text-zinc-400" title={t.timestamp ? new Date(t.timestamp * 1000).toLocaleString() : ""}>{timeAgo(t.timestamp)}</td>
                    <td className="px-4 py-2"><span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${TYPE_STYLE[t.type]}`}>{t.type}</span></td>
                    <td className="px-4 py-2"><Party address={t.from} /></td>
                    <td className="px-4 py-2"><Party address={t.to} /></td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-zinc-300">{fmt(t.amount)} {symbol}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasNextPage && (
          <div className="p-4 flex justify-center">
            <Button data-testid="token-transfers-load-more" variant="outline" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}
              className="border-[#2A2411] text-zinc-300 hover:text-white hover:bg-[#D4AF37]/10">
              {isFetchingNextPage ? <Loader2 size={14} className="animate-spin" /> : "Load more transfers"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
